import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { humanize } from '@/lib/format';
import type { EditorField, ResolvedField } from '@/pages/apps/designs/components/design-state';
import type { FieldType } from '@/types/platform';
import { Trash2 } from 'lucide-react';

interface FieldRowProps {
  field: ResolvedField;
  fieldTypes: FieldType[];
  onChange: (patch: Partial<EditorField>) => void;
  onRemove: () => void;
}

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: 'Short text',
  long_text: 'Long text',
  number: 'Number',
  money: 'Money',
  date: 'Date',
  choice: 'Choice',
  checkbox: 'Yes / No',
  email: 'Email',
  phone: 'Phone',
  customer: 'Customer',
  attachments: 'Photos and files',
  location: 'Map location',
};

const MAX_LABEL_LENGTH = 60;

const FieldRow = ({ field, fieldTypes, onChange, onRemove }: FieldRowProps) => {
  const labelId = `${field.id}-label`;
  const typeId = `${field.id}-type`;
  const requiredId = `${field.id}-required`;
  const optionsId = `${field.id}-options`;

  return (
    <li className="space-y-3 rounded-md border p-3">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_11rem_auto_auto] sm:items-end">
        <div className="space-y-1.5">
          <Label htmlFor={labelId}>Field name</Label>
          <Input
            id={labelId}
            value={field.label}
            onChange={(event) => onChange({ label: event.target.value })}
            maxLength={MAX_LABEL_LENGTH}
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={typeId}>Type</Label>
          <Select
            value={field.type}
            onValueChange={(value) => {
              const type = fieldTypes.find((candidate) => candidate === value);
              if (type) onChange({ type });
            }}
          >
            <SelectTrigger id={typeId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fieldTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {FIELD_TYPE_LABELS[type] ?? humanize(type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex h-9 items-center gap-2">
          <Switch
            id={requiredId}
            checked={field.required}
            onCheckedChange={(checked) => onChange({ required: checked })}
          />
          <Label htmlFor={requiredId}>Required</Label>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="justify-self-start"
          onClick={onRemove}
        >
          <Trash2 aria-hidden="true" />
          Remove<span className="sr-only"> field {field.label}</span>
        </Button>
      </div>

      {field.type === 'choice' ? (
        <div className="space-y-1.5">
          <Label htmlFor={optionsId}>Choices, separated by commas</Label>
          <Input
            id={optionsId}
            value={field.optionsText}
            onChange={(event) => onChange({ optionsText: event.target.value })}
            placeholder="Scheduled, In progress, Complete"
            required
          />
        </div>
      ) : null}

      <p className="text-muted-foreground text-xs">
        Key: <code>{field.key}</code>
      </p>
    </li>
  );
};

export default FieldRow;
